import React, { useCallback, useEffect, useState } from "react";
import { useSelector, connect, useDispatch } from "react-redux";
import { bindActionCreators } from "redux";
import { useLocation, Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import * as devicesActionCreators from "store/actions/devices";
import * as configActionCreators from "store/actions/config";
import * as callActionCreators from "store/actions/call";
import * as userActionCreators from "store/actions/user";
import * as googleAnalytics from "store/actions/googleAnalytics";
import { toggleSettingPopup } from "store/actions/app";
import storage from "utils/storage";
import Chat from "containers/Chat";
import Modal from "components/Modal";
import Settings from "containers/Settings";
import InviteToCallPopup from "../../containers/InviteToCall/Popup";
import { isCustomParamEnabled, test } from "utils/helpers";
import useLoki from "utils/useLoki";
import { isUserAuthorized } from "utils/login";
import {
  useKeyboardShortcut,
  useModerationStatuses,
  useCurrentUser,
  useInsightServerUrl,
  useShowJoinNotification,
  useShowLeaveNotification,
  useInactivityTimer,
  useShowInviteNotification,
  useOpenNCloseCCNotification,
} from "utils/hooks";
import "./GuestInCall.scss";
import { getEntityByRoomKey } from "services/SoapAPIProvider/soapAPIRequests/getEntityByRoomKey";
import { deviceDisableReason } from "utils/constants";
import { getLectureModeParticipantsRequest } from "services/SoapAPIProvider/soapAPIRequests/getLectureModeParticipants";
import EpicCallMediaCapture from "containers/EpicCallMediaCapture/EpicCallMediaCapture";
import { WebBreakoutRooms } from "features";
import { saveRoomPin, setCallEndTime } from "store/actions/call";
import { updateUser } from "store/actions/user";
import * as webViewActionCreators from "store/actions/webView";
import * as breakoutRoomsActonCreators from "../../features/BreakoutRooms/actions/creators";
import useVideoAudioContent from "containers/VideoAudioContent/useVideoAudioContent";
import useChatSpecialMessages from "containers/Chat/useChatSpecialMessages";
import { useFECC } from "utils/compositorHooks";
import Header from "./partials/Header/Header";
import Renderer from "./partials/Renderer/Renderer";
import Footer from "./partials/Footer/Footer";
import SidebarBox from "./partials/SidebarBox/SidebarBox";
import useWakeLock from "../../hooks/useWakeLock";
import ReconnectView from "containers/ReconnectView/ReconnectView";
import { TAB_INDEX_IN_CALL } from "../../utils/constants";
import useInterpreterPolling from "containers/InviteToCall/Popup/Tabs/Interpreter/hook/useInterpreterPolling";
import useSetInterpreterStatus from "containers/InviteToCall/Popup/Tabs/Interpreter/hook/useSetInterpreterStatus";
import {
  getInterpreterProvider,
  setURL,
} from "containers/InviteToCall/Popup/Tabs/Interpreter/providers/InterpreterProvider";
import { closedCCNotification } from "store/actions/call";
import { getCcButtonActive } from "store/selectors/call";
import {
  interpreterLanguages,
  interpreterGenderOption,
  interpreterCallTypeOption,
} from "store/actions/interpreter";
import { Logger } from "utils/logger";
const mapStateToProps = ({
  devices,
  call,
  config,
  user,
  feature_breakoutRooms,
  interpreter,
  app,
}) => ({
  isCallActive: call.active,
  isCallLeaving: call.leaving,
  disconnectReason: call.disconnectReason,
  participants: call.participants,
  callProperties: call.properties,
  callStartedTime: call.callStartedTime,
  selectedMicrophone: devices.selectedMicrophone,
  selectedSpeaker: devices.selectedSpeaker,
  selectedCamera: devices.selectedCamera,
  isCameraTurnedOn: devices.isCameraTurnedOn,
  isMicrophoneTurnedOn: devices.isMicrophoneTurnedOn,
  isSpeakerTurnedOn: devices.isSpeakerTurnedOn,
  isCameraDisabled: devices.isCameraDisabled,
  isMicrophoneDisabled: devices.isMicrophoneDisabled,
  isSpeakerDisabled: devices.isSpeakerDisabled,
  isStatisticsOverlaySet: config.isStatisticsOverlaySet,
  customParameters: config.customParameters,
  gcpServices: config.listOfGCPServices,
  moderationPanelOpened: call.moderationPanelOpened,
  userIsRegistered: user.isRegistered,
  userInfo: user.userInfo,
  roomInfo: call.roomInfo,
  urlWaitingRoomVideoContent: config.urlWaitingRoomVideoContent,
  urlWaitingRoomAudioContent: config.urlWaitingRoomAudioContent,
  urlWaitingRoomBackgroundContent: config.urlWaitingRoomBackgroundContent,
  urlDoNotSaveDisplayName: config.urlDoNotSaveDisplayName,
  extData: config.extData,
  hasExtData: config.hasExtData,
  extDataType: config.extDataType,
  cameraModerationState: devices.cameraModerationState,
  microphoneModerationState: devices.microphoneModerationState,
  urlModeratorPin: config.urlModeratorPin,
  localWindowShares: call.localWindowShares,
  remoteWindowShares: call.remoteWindowShares,
  isWebViewEnabled: config.urlInitializeWebView.value,
  portalFeatures: config.portalFeatures,
  sendCcAnalytics: call.cc.sendAnalytics,
  jwtToken: config.jwtToken,
  urlSkipParticipantNotifications: config.urlSkipParticipantNotifications.value,
  breakoutRooms: feature_breakoutRooms,
  urlInvitees: config.urlInvitees,
  networkConnectionStatus: app.networkConnectionStatus,
});

const mapDispatchToProps = (dispatch) => ({
  ...bindActionCreators(callActionCreators, dispatch),
  ...bindActionCreators(devicesActionCreators, dispatch),
  ...bindActionCreators(configActionCreators, dispatch),
  ...bindActionCreators(googleAnalytics, dispatch),
  ...bindActionCreators(userActionCreators, dispatch),
  ...bindActionCreators(webViewActionCreators, dispatch),
  ...bindActionCreators(breakoutRoomsActonCreators, dispatch),
});

const GuestInCall = ({
  isCallActive,
  isCallLeaving,
  disconnectReason,
  participants,
  callProperties,
  callStartedTime,
  endCall,
  cameraTurnOff,
  isCameraTurnedOn,
  microphoneTurnOff,
  microphoneTurnOn,
  speakerTurnOn,
  speakerTurnOff,
  enablePreview,
  isStatisticsOverlaySet,
  setStatisticsOverlay,
  getCustomParameters,
  customParameters,
  gcpServices,
  moderationPanelOpened,
  userIsRegistered,
  setRoomInfo,
  roomInfo,
  userInfo,
  urlDoNotSaveDisplayName,
  extData,
  extDataType,
  cameraModerationState,
  microphoneModerationState,
  saveParticipantsDetails,
  logout,
  hasExtData,
  urlModeratorPin,
  closeModerationPanel,
  localWindowShares,
  remoteWindowShares,
  showSharePreview,
  setDisableTabletParticipantLimitRestrictions,
  isWebViewEnabled,
  getWebViewDevicesState,
  portalFeatures,
  sendCcAnalytics,
  setCcSendAnalytics,
  jwtToken,
  urlSkipParticipantNotifications,
  breakoutRooms,
  selectedMicrophone,
  selectedSpeaker,
  selectedCamera,
  markExtDataInvteeAsJoined,
  interpreterSessionId,
}) => {
  const [isSidebarOpen, setSidebarState] = useState(false);
  const [isChatOpen, setChatState] = useState(false);
  const areSettingsRendered = useSelector(
    (state) => state.app.areSettingsRendered
  );
  const [invitePopupOpened, setInvitePopupOpened] = useState(false);
  const call = useSelector((state) => state.call);

  const dispatch = useDispatch();

  const { t } = useTranslation();
  const location = useLocation();
  const [showJoinNotification] = useShowJoinNotification();
  const [showLeaveNotification] = useShowLeaveNotification();
  const [showInviteNotification, hideInviteNotification] =
    useShowInviteNotification();
  const [
    showOpenCCNotification,
    showCloseCCNotification,
    removeCCNotification,
  ] = useOpenNCloseCCNotification();
  const [dataForAudioVideoContent, setDataForAudioVideoContent] =
    useVideoAudioContent();
  const { isUserAdmin, isUserRoomOwner, isBecomeModerator } =
    useModerationStatuses();
  const currentUser = useCurrentUser();
  const insightServerUrl = useInsightServerUrl();
  useLoki(isCallActive, insightServerUrl);
  useInactivityTimer(customParameters, participants, endCall);
  useChatSpecialMessages(currentUser, participants, t);
  useFECC(isWebViewEnabled);

  const interpreterStatus = useSetInterpreterStatus();
  const callName = callProperties.callName || location.state?.roomKey;
  const afterCallStarted = (new Date().getTime() - callStartedTime) / 1000 > 5;
  const { authToken, portal } = storage.getItem("user") || {};
  const epicServiceAvailable = gcpServices?.epicService?.isServiceAvailable;
  const isEpicServiceEnabled = isCustomParamEnabled(
    userIsRegistered ? customParameters?.epicServiceEnabled : null
  );

  const isCCEnabled =
    portalFeatures?.CcEnabled &&
    gcpServices?.closedCaption?.isServiceAvailable &&
    gcpServices?.closedCaption?.url &&
    !isCustomParamEnabled(customParameters?.closedCaptioningDisabled) &&
    jwtToken;
  const isBrRoomsEnabled = portalFeatures?.BrEnabled;
  const isInterpreterServiceAvilable =
    portalFeatures?.CcEnabled &&
    gcpServices?.interpreterService?.isServiceAvailable &&
    gcpServices?.interpreterService?.url &&
    isCustomParamEnabled(customParameters?.isInterpreterFeatureSupported);

  const isEnoughWindowSpace = useCallback(() => {
    let value = 480;

    if (isBrRoomsEnabled && isCCEnabled) {
      value += 140;
    }

    return window.innerWidth > 360 + value + 300;
  }, [isBrRoomsEnabled, isCCEnabled]);

  const toggleSettings = useCallback(() => {
    dispatch(toggleSettingPopup());
  }, [dispatch]);

  const toggleInviteToCallPopup = useCallback(() => {
    setInvitePopupOpened(!invitePopupOpened);
  }, [invitePopupOpened]);

  const toggleSidebar = useCallback(() => {
    if (isChatOpen && !isEnoughWindowSpace() && !isSidebarOpen) {
      setChatState(false);
    }
    setSidebarState(!isSidebarOpen);
  }, [isChatOpen, isEnoughWindowSpace, isSidebarOpen]);

  const toggleChat = useCallback(() => {
    if (isSidebarOpen && !isEnoughWindowSpace() && !isChatOpen) {
      setSidebarState(false);
    }
    setChatState(!isChatOpen);
  }, [isChatOpen, isEnoughWindowSpace, isSidebarOpen]);

  const toggleAnnouncement = useCallback(() => {
    if (isSidebarOpen && !isEnoughWindowSpace()) {
      setSidebarState(false);
    }
  }, [isEnoughWindowSpace, isSidebarOpen]);

  useEffect(() => {
    if (localWindowShares?.length) {
      localWindowShares.forEach((share) => {
        share.SetPreviewLabel({ previewLabel: t("MY_SHARE") });
        if (share.SetEndShareLabel) {
          share.SetEndShareLabel({ endLabel: t("END_SHARE") });
        }
      });
    }
  }, [localWindowShares, t]);

  useEffect(() => {
    if (isInterpreterServiceAvilable) {
      setURL(gcpServices?.interpreterService?.url);
      (async () => {
        const interpreterConfig =
          await getInterpreterProvider()?.getConfiguration();
        if (interpreterConfig) {
          dispatch(
            interpreterGenderOption(interpreterConfig.genderSelectionOption)
          );
          dispatch(
            interpreterCallTypeOption(interpreterConfig.callTypeOptions)
          );
          dispatch(interpreterLanguages(interpreterConfig.languageList));
        }
      })();
    }
    // eslint-disable-next-line
  }, [isInterpreterServiceAvilable]);

  useEffect(() => {
    const isCameraMutedByModeration =
      (cameraModerationState.moderationType ===
        deviceDisableReason.HARD_MUTED &&
        cameraModerationState.state) ||
      (cameraModerationState.moderationType ===
        deviceDisableReason.SOFT_MUTED &&
        cameraModerationState.state);

    if (isCameraMutedByModeration) {
      cameraTurnOff();
    }
  }, [
    cameraModerationState.moderationType,
    cameraModerationState.state,
    cameraTurnOff,
  ]);

  useEffect(() => {
    remoteWindowShares.forEach((share) => {
      const shareLabel = document.querySelector(
        `.application-type.remote-track[data-participant-id="${share?.participant?.id}"] .video-display-name-main`
      );
      if (shareLabel) {
        shareLabel.innerHTML = `${share.participant.name} (${t("SHARE_NOUN")})`;
      }
    });
    setTimeout(() => {
      let popupApplicationImg = document.querySelectorAll(
        ".popup-application img"
      );
      if (popupApplicationImg) {
        popupApplicationImg.forEach((ele) => {
          ele.setAttribute("aria-label", "popup application");
          ele.setAttribute("alt", "popup application");
        });
      }
    }, 1000);
  }, [remoteWindowShares, t]);

  useEffect(() => {
    if (isWebViewEnabled) return; // set device state according to Epic Monitor device state
    if (location.state?.isMicrophoneTurnedOn) {
      microphoneTurnOn();
    } else {
      microphoneTurnOff();
    }
  }, [location, microphoneTurnOn, microphoneTurnOff, isWebViewEnabled]);

  useEffect(() => {
    const isMicrophoneMutedByModeration =
      (microphoneModerationState.moderationType ===
        deviceDisableReason.HARD_MUTED &&
        microphoneModerationState.state) ||
      (microphoneModerationState.moderationType ===
        deviceDisableReason.SOFT_MUTED &&
        microphoneModerationState.state);

    if (isMicrophoneMutedByModeration) {
      microphoneTurnOff();
    }
  }, [
    microphoneModerationState.moderationType,
    microphoneModerationState.state,
    microphoneTurnOff,
  ]);

  useEffect(() => {
    if (isWebViewEnabled) return; // set device state according to Epic Monitor device state
    if (location.state?.isSpeakerTurnedOn) {
      speakerTurnOn();
    } else {
      speakerTurnOff();
    }
  }, [location, speakerTurnOn, speakerTurnOff, isWebViewEnabled]);

  useEffect(() => {
    if (isWebViewEnabled) {
      // Get devices state from Epic Monitor and apply appropriate state for our audio devices
      getWebViewDevicesState();
    }
  }, [location, isWebViewEnabled, getWebViewDevicesState]);

  useEffect(() => {
    enablePreview(true);
    showSharePreview(true);
  }, [enablePreview, showSharePreview]);

  useEffect(() => {
    if (isCallActive && !urlDoNotSaveDisplayName.value) {
      storage.addItem("displayName", location.state?.displayName);
    }
  }, [location, isCallActive, urlDoNotSaveDisplayName.value]);

  useEffect(() => {
    const participant = participants.participantJoined;
    if (
      participant &&
      !participant.isLocal &&
      afterCallStarted &&
      !urlSkipParticipantNotifications
    ) {
      showJoinNotification(participant.name);
      hideInviteNotification(participant.name);
      markExtDataInvteeAsJoined(participant.name);
    }
    // eslint-disable-next-line
  }, [participants.participantJoined]);

  useEffect(() => {
    const participant = participants.participantLeft;
    if (
      participant &&
      !participant.isLocal &&
      isCallActive &&
      !isCallLeaving &&
      !urlSkipParticipantNotifications &&
      !breakoutRooms.isTransferInProgress
    ) {
      showLeaveNotification(participant.name);
    }
    // eslint-disable-next-line
  }, [participants.participantLeft]);

  useEffect(() => {
    return () => {
      setStatisticsOverlay(false);
    };
  }, [setStatisticsOverlay]);

  useEffect(() => {
    // todo, probably we need to remove this, we get the custom params on init screen, but need to double check
    const host = location.state.host || portal;
    const isUserAuthorizedValue = isUserAuthorized(host);
    console.log("Is user authorized:", isUserAuthorizedValue);

    if (location.state.isCustomParamsReceived && !isUserAuthorizedValue) return;

    const reqParams = {
      host,
    };
    if (isUserAuthorizedValue) {
      reqParams["authToken"] = authToken;
    }
    getCustomParameters(reqParams);
    // eslint-disable-next-line
  }, [getCustomParameters, isUserAuthorized]);

  useEffect(() => {
    setDisableTabletParticipantLimitRestrictions(true);
    if (userIsRegistered) {
      getEntityByRoomKey(portal, authToken, location.state?.roomKey).then(
        (data) => {
          setRoomInfo(data);
        }
      );
    }
    setTimeout(() => {
      window.loadElement(TAB_INDEX_IN_CALL);
      window.setFocus(0);
    }, 1000);
    return () => {
      logout();
      interpreterStatus.interpreterLeft();
      dispatch(closedCCNotification());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    showInviteNotification();
    return () => {
      removeCCNotification();
    };
    // eslint-disable-next-line
  }, []);

  const showCCNotification =
    isUserAuthorized(location.state?.host) &&
    (isUserAdmin || isUserRoomOwner || isBecomeModerator);
  const ccBtnActive = useSelector(getCcButtonActive);

  useEffect(() => {
    const ccLogger = new Logger("CC");
    if (!showCCNotification) {
      ccLogger.log("Notification triggered user is not moderator");
      return;
    }
    ccLogger.log("Notification triggered user is moderator");
    if (
      call.showCCOpenedNotification &&
      !ccBtnActive &&
      !call.cc.requestInProgress &&
      localStorage.enableCcNotification === "true"
    ) {
      showOpenCCNotification();
      ccLogger.log("Moderator display open notification");
    }
    if (
      call.showCCClosedNotification &&
      !ccBtnActive &&
      !call.cc.requestInProgress &&
      localStorage.enableCcNotification === "true"
    ) {
      showCloseCCNotification();
      ccLogger.log("Moderator display close notification");
    }
    if (call.showCCOpenedNotification || call.showCCClosedNotification) {
      dispatch(closedCCNotification());
    }
  }, [
    call,
    showCCNotification,
    showOpenCCNotification,
    showCloseCCNotification,
    ccBtnActive,
    dispatch,
  ]);

  useEffect(() => {
    return () => {
      if (sendCcAnalytics) {
        dispatch(googleAnalytics.closedCaptionCallEnd());
        setCcSendAnalytics(false);
      }
    };
  }, [dispatch, sendCcAnalytics, setCcSendAnalytics]);

  useEffect(() => {
    if (
      extData &&
      extDataType === "1" &&
      roomInfo?.entityID &&
      urlModeratorPin.value
    ) {
      getLectureModeParticipantsRequest(
        portal,
        authToken,
        roomInfo?.entityID,
        urlModeratorPin.value
      )
        .then((res) => {
          if (res) {
            dispatch(
              updateUser({
                accountType: "admin",
                becomeModerator: true,
              })
            );
            dispatch(saveRoomPin(urlModeratorPin.value));
          }
        })
        .catch((e) => {
          console.error("Failed check urlModeratorPin", e);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomInfo.entityID]);

  /*****
   * In case Siderbar and chat is open and window is not enough space width then close the chat popup
   *
   */
  useEffect(() => {
    function onResizeWindow() {
      if (isSidebarOpen && !isEnoughWindowSpace() && isChatOpen) {
        setChatState(false);
      }
    }
    window.addEventListener("resize", onResizeWindow);
    return () => {
      window.removeEventListener("resize", onResizeWindow);
    };
  }, [isSidebarOpen, isChatOpen, isEnoughWindowSpace]);

  useEffect(() => {
    if (roomInfo?.entityID && isUserAdmin && +roomInfo?.RoomMode?.roomPIN) {
      getLectureModeParticipantsRequest(
        portal,
        authToken,
        roomInfo?.entityID,
        roomInfo?.RoomMode?.roomPIN
      ).then((res) => {
        if (res) {
          saveParticipantsDetails(
            res?.Envelope?.Body?.GetLectureModeParticipantsResponse
              ?.LectureModeParticipant
          );
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [participants.list, isUserAdmin, roomInfo?.RoomMode?.roomPIN]);

  useEffect(() => {
    if (selectedSpeaker?.name) {
      dispatch(
        googleAnalytics.roomJoinWithSpeakerDeviceName(selectedSpeaker.name)
      );
    }
    if (selectedMicrophone?.name) {
      dispatch(
        googleAnalytics.roomJoinWithMicrophoneDeviceName(
          selectedMicrophone.name
        )
      );
    }
    if (selectedCamera?.name) {
      dispatch(
        googleAnalytics.roomJoinWithCameraDeviceName(selectedCamera.name)
      );
    }
    // eslint-disable-next-line
  }, [dispatch]);

  useKeyboardShortcut(
    {
      shiftKey: true,
      ctrlKey: true,
      code: "KeyS",
    },
    () => {
      setStatisticsOverlay(!isStatisticsOverlaySet);
    }
  );
  useWakeLock();
  useInterpreterPolling();
  useEffect(() => {
    if (!isUserAdmin && !isUserRoomOwner && moderationPanelOpened) {
      closeModerationPanel();
    }
  }, [
    closeModerationPanel,
    isUserAdmin,
    isUserRoomOwner,
    moderationPanelOpened,
  ]);

  useEffect(() => {
    if (isCallLeaving) {
      dispatch(setCallEndTime(Date.now()));
    }
  }, [isCallLeaving, dispatch]);
  if (isCallLeaving || disconnectReason) {
    return (
      <Navigate replace to={"/LeavingCallScreen"} state={location.state} />
    );
  }

  return (
    <div
      className={`in-call${isSidebarOpen ? " sidebar-is-open" : ""}${
        isChatOpen ? " chat-is-open" : ""
      }`}
      {...test("IN_CALL_MARKER")}
    >
      <ReconnectView></ReconnectView>
      <SidebarBox
        isSidebarOpen={isSidebarOpen}
        callName={callName}
        userIsRegistered={userIsRegistered}
        toggleSidebar={toggleSidebar}
      />
      <div
        className={`call-screen ${
          isWebViewEnabled ? "call-screen--webview" : ""
        }`}
      >
        <Header
          participantsLength={participants.list.length}
          isSidebarOpen={isSidebarOpen}
          toggleSidebar={toggleSidebar}
          callName={callName}
          toggleSettings={toggleSettings}
          isWebViewEnabled={isWebViewEnabled}
        />
        <Renderer
          dataForAudioVideoContent={dataForAudioVideoContent}
          isCameraTurnedOn={isCameraTurnedOn}
          setDataForAudioVideoContent={setDataForAudioVideoContent}
          moderationPanelOpened={moderationPanelOpened}
          userIsRegistered={userIsRegistered}
          toggleAnnouncement={toggleAnnouncement}
        />
        <Footer
          isWebViewEnabled={isWebViewEnabled}
          portalFeatures={portalFeatures}
          isBrRoomsEnabled={isBrRoomsEnabled}
          isCCEnabled={isCCEnabled}
          toggleInviteToCallPopup={toggleInviteToCallPopup}
          dataForAudioVideoContent={dataForAudioVideoContent}
          isChatOpen={isChatOpen}
          toggleChat={toggleChat}
        />
      </div>
      <Chat isChatOpen={isChatOpen} toggleChat={toggleChat} />
      <Modal>
        {areSettingsRendered && <Settings onClose={toggleSettings} />}
      </Modal>
      <Modal>
        {invitePopupOpened && (
          <InviteToCallPopup
            onClose={toggleInviteToCallPopup}
            userIsRegistered={userIsRegistered}
            roomInfo={roomInfo}
            userInfo={userInfo}
            customParameters={customParameters}
            gcpServices={gcpServices}
          />
        )}
      </Modal>
      {isCallActive &&
        epicServiceAvailable &&
        isEpicServiceEnabled &&
        hasExtData &&
        +extDataType === 1 && <EpicCallMediaCapture />}
      {isBrRoomsEnabled && <WebBreakoutRooms />}
    </div>
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(GuestInCall);
